import { newGlobalFileServer } from "../../axiosInstance";

const encodePathSegments = (path) =>
  path
    .split("/")
    .filter(Boolean)
    .map((segment) => {
      try {
        return encodeURIComponent(decodeURIComponent(segment));
      } catch {
        return encodeURIComponent(segment);
      }
    })
    .join("/");

/**
 * Builds a CDN URL for a product media path (same pattern as the rest of the app).
 * Path segments are percent-encoded so reserved characters (#, ?, etc.) and
 * non-ASCII filenames resolve correctly and stay valid inside `srcset` values.
 * @param {string} path
 * @returns {string}
 */
export const getProductImageUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("data:")) return path;
  if (path.startsWith(newGlobalFileServer)) {
    const relative = path.slice(newGlobalFileServer.length);
    return `${newGlobalFileServer}${encodePathSegments(relative)}`;
  }
  if (/^https?:\/\//i.test(path)) return path;

  const cleanPath = path.startsWith("/") ? path.slice(1) : path;
  return `${newGlobalFileServer}${encodePathSegments(cleanPath)}`;
};

/**
 * Sizes pre-generated by pim-be (S3 subscriber + backfill). Contract:
 * `X-origin.{ext}` → `X-{size}.{ext}`; legacy `X.{ext}` → `X-{size}.{ext}`.
 */
const VARIANT_SIZES = [600, 300, 150];
// Must match pim-be's imageVariants.ts: variants are only generated for
// formats whose re-encoded output still matches the key extension.
const RESIZABLE_EXTENSIONS = ["jpg", "jpeg", "png"];
const ORIGIN_SUFFIX = "-origin";

const splitImageUrl = (url) => {
  const dot = url.lastIndexOf(".");
  if (dot <= 0 || dot === url.length - 1) return null;
  const base = url.slice(0, dot);
  const ext = url.slice(dot + 1);
  if (ext.includes("/") || !RESIZABLE_EXTENSIONS.includes(ext.toLowerCase())) {
    return null;
  }
  return { base, ext };
};

/**
 * CDN URL of the pre-generated `size` variant; falls back to the plain
 * URL whenever a variant can't be derived (foreign host, data URI,
 * unknown extension/size). Pair with an onError fallback to the original.
 * @param {string} pathOrUrl
 * @param {number} size
 * @returns {string}
 */
export const getResizedProductImageUrl = (pathOrUrl, size) => {
  const url = getProductImageUrl(pathOrUrl);
  if (!url || !VARIANT_SIZES.includes(size)) return url;
  if (pathOrUrl.startsWith("data:")) return url;
  if (!url.startsWith(newGlobalFileServer)) return url;

  const parts = splitImageUrl(url);
  if (!parts) return url;

  let base = parts.base;
  for (const variantSize of VARIANT_SIZES) {
    const suffix = `-${variantSize}`;
    if (base.endsWith(suffix)) {
      base = base.slice(0, -suffix.length);
      break;
    }
  }
  if (base.endsWith(ORIGIN_SUFFIX)) {
    base = base.slice(0, -ORIGIN_SUFFIX.length);
  }
  return `${base}-${size}.${parts.ext}`;
};

/** `["url1", "url2"]` → `"url1 1x, url2 2x"`; undefined if any entry is missing. */
const buildDprSrcSet = (urls) =>
  urls.every(Boolean)
    ? urls.map((url, index) => `${url} ${index + 1}x`).join(", ")
    : undefined;

/**
 * URL of the `.webp` sibling pim-be stores next to every JPEG/PNG variant,
 * or undefined when no variant can be derived for the path. Serve via
 * `<picture><source type="image/webp">` with the JPEG/PNG variant as the
 * `<img>` fallback.
 * @param {string} pathOrUrl
 * @param {number} size
 * @returns {string | undefined}
 */
export const getWebpVariantUrl = (pathOrUrl, size) => {
  const variantUrl = getResizedProductImageUrl(pathOrUrl, size);
  // Derivable iff the URL actually carries the requested variant suffix —
  // this also holds when the source path was ALREADY that size's variant
  // (the webp sibling exists for it too), unlike comparing to the original.
  const lowerUrl = (variantUrl || "").toLowerCase();
  if (
    !variantUrl ||
    !RESIZABLE_EXTENSIONS.some((ext) => lowerUrl.endsWith(`-${size}.${ext}`))
  ) {
    return undefined;
  }
  return variantUrl.replace(/\.[^.]+$/, ".webp");
};

/**
 * WebP `srcSet` (1x/2x/…) or undefined when any size is underivable.
 * @param {string} pathOrUrl
 * @param {number[]} sizes
 * @returns {string | undefined}
 */
export const getProductImageWebpSrcSet = (pathOrUrl, sizes = [150, 300]) =>
  buildDprSrcSet(sizes.map((size) => getWebpVariantUrl(pathOrUrl, size)));

/**
 * `srcSet` string (1x/2x/…) or undefined when no variant can be derived.
 * @param {string} pathOrUrl
 * @param {number[]} sizes
 * @returns {string | undefined}
 */
export const getProductImageSrcSet = (pathOrUrl, sizes = [150, 300]) => {
  const original = getProductImageUrl(pathOrUrl);
  const urls = sizes.map((size) => getResizedProductImageUrl(pathOrUrl, size));
  if (urls.every((url) => url === original)) return undefined;
  return buildDprSrcSet(urls);
};

/**
 * Same-origin proxy for cropper canvas export.
 * Dev: `src/setupProxy.js` · Production: CloudFront `/pim-images/*` in `sst.config.ts`.
 * Serves the same image bytes as the CDN — visually identical to thumbnails.
 */
export const getCropperProxyUrl = (pathOrUrl) => {
  if (!pathOrUrl) return null;
  if (pathOrUrl.startsWith("data:")) return null;

  let relativePath = pathOrUrl;

  if (pathOrUrl.startsWith(newGlobalFileServer)) {
    relativePath = pathOrUrl.slice(newGlobalFileServer.length);
  } else if (/^https?:\/\//i.test(pathOrUrl)) {
    return null;
  }

  if (relativePath.startsWith("/")) {
    relativePath = relativePath.slice(1);
  }

  if (!relativePath) return null;

  return `/pim-images/${encodePathSegments(relativePath)}`;
};

/** @returns {boolean} */
export const canRecropProductImage = (pathOrUrl) => {
  if (!pathOrUrl || pathOrUrl.startsWith("data:")) return false;
  return Boolean(getProductImageUrl(pathOrUrl));
};

/**
 * Cropper source URL: same-origin proxy when possible (enables preview export + save).
 */
export const getCropperImageUrl = (pathOrUrl) =>
  getCropperProxyUrl(pathOrUrl) || getProductImageUrl(pathOrUrl);

/** True when canvas export (save) is possible for this cropper src. */
export const canExportCropperCanvas = (src) =>
  typeof src === "string" && (src.startsWith("data:") || src.startsWith("/"));
